import { DeleteOutlined, EditOutlined, SaveOutlined } from "@ant-design/icons";
import { DragEndEvent } from "@dnd-kit/core";
import { arrayMove } from "@dnd-kit/sortable";
import { Button, DatePicker, Image, Spin, Switch } from "antd";
import { ColumnsType } from "antd/es/table";
import DraggableTable from "components/dragable-table";
import { COMMON_STATUS, DIRECTION_TYPE } from "constants/constants";
import { APP_PATH } from "constants/path";
import useResponsive from "hooks/useResponsive";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { queryDefault, setHotSaleFilter } from "store/filter/index.slice";
import { translate } from "utils";
import { formatCapital, formatDateTimeString, formatPrice } from "utils/format";
import ModalCreate from "./components/ModalCreate";
import Search, { FormatQueryRef } from "./components/Search";
import { HotSale, HotSaleQueryParams } from "./components/type";
import {
	deleteHotSaleAsync,
	getListHotSaleAsync,
	selectHotSaleList,
	selectHotSaleTotal,
	setListHotSale,
	updateHotSaleAsync,
	updateHotSaleItemInList,
	updateOrderHotSaleAsync,
	updateStatusHotSaleAsync,
} from "./redux/hot-sale.slice";
import { PERMISSION } from "../../constants/permission";
import validatePerms from "utils/validate-perms";
import moment from "moment";

const defaultCarAvatar = "/images/default-car.png";

const HotSales = () => {
	const dispatch = useDispatch();
	const { isMobile } = useResponsive();
	const [isLoading, setIsLoading] = useState(false);
	const [query, setQuery] = useState<HotSaleQueryParams>(queryDefault);
	const [isOpenCreateModal, setIsOpenCreateModal] = useState<boolean>(false);
	const hotSales = useSelector(selectHotSaleList);
	const hotSaleTotal = useSelector(selectHotSaleTotal);
	const formatQueryRef = useRef<FormatQueryRef>(null);
	const [editingRecords, setEditingRecords] = useState<
		{ id: number; startDate?: string; endDate?: string }[]
	>([]);
	const hasPermssionUpdate = useRef(validatePerms([PERMISSION.HOTSALE.UPDATE]));

	const columns: ColumnsType<HotSale> = [
		{
			title: translate("No"),
			dataIndex: "order",
			key: "order",
			width: "50px",
			align: "center",
		},
		{
			title: translate("Image"),
			dataIndex: ["car", "cover"],
			key: "image",
			width: 200,
			render: (image, record) => (
				<div className="flex justify-center items-center w-full ">
					<Image
						className="object-cover aspect-video"
						src={image ?? defaultCarAvatar}
						alt={`Hot sales ${record.carId}`}
					/>
				</div>
			),
		},
		{
			title: translate("Car title"),
			dataIndex: ["car", "name"],
			key: "title",
			width: "15%",
			render: (value, record) => (
				<Link to={`${APP_PATH.cars}/${record.carId}`} className="text-blue-500">
					<p>{value}</p>
				</Link>
			),
		},
		{
			title: translate("Merchant"),
			dataIndex: ["car", "merchant", "fullName"],
			key: "merchant",
			width: "15%",
		},
		{
			title: translate("Price"),
			dataIndex: ["car", "price"],
			key: "price",
			render: value => <p>{formatPrice(value)}</p>,
		},
		{
			title: translate("Car status"),
			dataIndex: ["car", "status"],
			key: "carStatus",
			width: 200,
			render: value => <p>{translate(formatCapital(value))}</p>,
		},
		{
			title: translate("Start date"),
			dataIndex: "startDate",
			key: "startDate",
			width: "250px",
			render: (value, record) =>
				isEditing(record) ? (
					<DatePicker
						className="w-full"
						format="DD/MM/YYYY"
						onChange={value => {
							setEditingRecords(
								editingRecords.map(r => {
									if (r.id === record.carId) {
										return {
											...r,
											startDate: value?.format("YYYY-MM-DD"),
										};
									}
									return r;
								}),
							);
						}}
						disabledDate={current => {
							const today = moment().subtract(1, "day").endOf("day")
							const end = editingRecords.find(
								r => r.id === record.carId,
							)?.endDate;
							if(end) {
								const endDate = end
								? moment(end)
								: moment().subtract(1, "day").endOf("day");
								return current < today || current > endDate;
								
							}

							return current < today
						}}
						defaultValue={moment(record.startDate)}
						// disabled={moment}
					/>
				) : (
					<p>{formatDateTimeString(value, true)}</p>
				),
		},
		{
			title: translate("End date"),
			dataIndex: "endDate",
			key: "endDate",
			width: "250px",
			render: (value, record) =>
				isEditing(record) ? (
					<DatePicker
						className="w-full"
						format="DD/MM/YYYY"
						onChange={value => {
							setEditingRecords(
								editingRecords.map(r => {
									if (r.id === record.carId) {
										return {
											...r,
											endDate: value?.format("YYYY-MM-DD"),
										};
									}
									return r;
								}),
							);
						}}
						disabledDate={current => {
							const start = editingRecords.find(
								r => r.id === record.carId,
							)?.startDate;
							const startDate = start
								? moment(start)
								: moment().subtract(1, "day").endOf("day");

							return current < startDate;
						}}
						defaultValue={moment(record.endDate)}
					/>
				) : (
					<p>{formatDateTimeString(value, true)}</p>
				),
		},
		{
			title: translate("Status"),
			dataIndex: "status",
			key: "status",
			render: (value, record) => (
				<Switch
					checkedChildren={translate("Enable")}
					unCheckedChildren={translate("Disable")}
					checked={value === COMMON_STATUS.enabled.value}
					disabled={!hasPermssionUpdate.current || moment(record.endDate).isBefore(moment().startOf("day"))}
					onClick={(checked: boolean) => {
						window.dialogConfirm(
							translate("Confirm status change"),
							translate("Do you want to change the status?"),
							(isConfirm: boolean) => {
								if (isConfirm) {
									setIsLoading(true);
									dispatch(
										updateStatusHotSaleAsync({
											carId: record.carId,
											status: checked
												? COMMON_STATUS.enabled.value
												: COMMON_STATUS.disabled.value,
											callback: () => {
												setIsLoading(false);
												handleGetListHotSale(query);
											},
										}),
									);
								}
							},
						);
					}}
				/>
			),
		},
		{
			title: translate("Action"),
			dataIndex: "action",
			key: "action",
			render: (_, record) => (
				<div className="flex justify-center gap-2">
					{isEditing(record) ? (
						<Button
							type="primary"
							className="m-1 flex items-center justify-center bg-green-400 border border-green-400 hover:!bg-green-500 hover:!border-green-500"
							icon={<SaveOutlined />}
							disabled={!hasPermssionUpdate.current}
							shape="circle"
							size={isMobile ? "small" : "middle"}
							onClick={() => {
								const hotSale = editingRecords.find(r => r.id === record.carId);
								if (hotSale?.startDate && hotSale?.endDate) {
									dispatch(
										updateHotSaleAsync({
											carId: hotSale.id,
											startDate: hotSale.startDate,
											endDate: hotSale.endDate,
										}),
									);
									dispatch(
										updateHotSaleItemInList({
											carId: hotSale.id,
											startDate: hotSale.startDate,
											endDate: hotSale.endDate,
										}),
									);
									setEditingRecords(
										editingRecords.filter(rec => rec.id !== record.carId),
									);
								}
							}}
						/>
					) : (
						<Button
							danger
							type="primary"
							className="m-1 flex items-center justify-center bg-red-600"
							icon={<DeleteOutlined />}
							disabled={!hasPermssionUpdate.current}
							shape="circle"
							size={isMobile ? "small" : "middle"}
							onClick={() => {
								window.dialogConfirm(
									translate("Delete confirmation"),
									translate("Do you want to delete this notification?"),
									(isConfirm: boolean) => {
										if (isConfirm) {
											setIsLoading(true);
											dispatch(
												deleteHotSaleAsync({
													carId: record.carId,
													callback: () => {
														handleGetListHotSale(query);
														setIsLoading(false);
													},
												}),
											);
										}
									},
								);
							}}
						/>
					)}
					<Button
						type="primary"
						className="m-1 flex items-center justify-center bg-blue-400"
						icon={<EditOutlined />}
						disabled={
							!hasPermssionUpdate.current ||
							moment(record.endDate).isBefore(moment().startOf("day"))
						}
						shape="circle"
						size={isMobile ? "small" : "middle"}
						onClick={() => {
							handleClickEditHotSale(record);
						}}
					/>
				</div>
			),
		},
		{
			title: translate("Sort"),
			dataIndex: "sort",
			key: "sort",
		},
	];

	useEffect(() => {
		handleGetListHotSale(query);
	}, []);

	const isEditing = (hotSale: HotSale) =>
		editingRecords.map(r => r.id).includes(hotSale.carId);

	const handleClickEditHotSale = (hotSale: HotSale) => {
		if (!editingRecords.map(r => r.id).includes(hotSale.carId)) {
			setEditingRecords([
				...editingRecords,
				{
					id: hotSale.carId,
					startDate: hotSale.startDate,
					endDate: hotSale.endDate,
				},
			]);
		}
	};

	const handleGetListHotSale = (
		newQuery: HotSaleQueryParams,
		callback?: () => void,
	) => {
		setIsLoading(true);
		const configQuery = {
			...(formatQueryRef.current
				? formatQueryRef.current?.handleFormatQuery(newQuery)
				: newQuery),
		};
		dispatch(
			getListHotSaleAsync({
				query: configQuery,
				callback: () => {
					setIsLoading(false);
					setQuery(newQuery);
					dispatch(setHotSaleFilter(newQuery));
					callback && callback();
				},
			}),
		);
	};

	const handleChangePage = (page: number) => {
		const newQuery = { ...query, skip: (page - 1) * query.take };
		handleGetListHotSale(newQuery);
	};

	const handlePageSizeChange = (_: number, size: number) => {
		setTimeout(() => {
			const newQuery = { ...query, skip: 0, take: size };
			handleGetListHotSale(newQuery);
		}, 0);
	};

	const onDragEnd = ({ active, over }: DragEndEvent) => {
		if (active && over && active.id !== over?.id) {
			const activeIndex = hotSales.findIndex(item => item.carId === active.id);
			const overIndex = hotSales.findIndex(item => item.carId === over?.id);
			const newArray = arrayMove(hotSales, activeIndex, overIndex);
			const findNewOrderItem = hotSales.find(item => item.carId === over?.id);
			dispatch(
				updateOrderHotSaleAsync({
					carId: Number(active.id),
					newOrder: Number(findNewOrderItem?.order),
				}),
			);
			dispatch(
				setListHotSale({
					data: newArray,
					total: hotSaleTotal,
				}),
			);
		}
	};

	return (
		<div className="h-full bg-white p-5 my-5 fade-in">
			<Search
				query={query}
				setQuery={setQuery}
				getList={handleGetListHotSale}
				setOpenCreateModal={setIsOpenCreateModal}
				fowardRef={formatQueryRef}
			/>
			<div className="mt-10">
				<Spin spinning={isLoading}>
					<DraggableTable
						rowKey="carId"
						onDragEnd={onDragEnd}
						canDrag={hasPermssionUpdate.current}
						dragDirection={DIRECTION_TYPE.VERTICAL}
						dataSource={hotSales}
						columns={columns}
						scroll={{ x: 1000 }}
						pagination={{
							pageSizeOptions: ["5", "10", "20", "50"],
							total: hotSaleTotal,
							current: query.skip / query.take + 1,
							pageSize: query.take,
							showSizeChanger: true,
							showTotal: total =>
								translate("Total") + " " + total + " " + translate("results"),
							onChange: handleChangePage,
							onShowSizeChange: handlePageSizeChange,
						}}
						locale={{
							emptyText: (
								<span className="text-gray-8">{translate("No data")}</span>
							),
						}}
					/>
				</Spin>
			</div>
			<ModalCreate
				isOpen={isOpenCreateModal}
				setIsOpen={setIsOpenCreateModal}
				getList={() => handleGetListHotSale(queryDefault)}
			/>
		</div>
	);
};

export default HotSales;
